import { useApolloClient, useQuery } from '@apollo/client';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PostRow from '../post/PostRow';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import { gql } from '../graphql/generated/gql';

gql(`
  fragment useViewedPostsColumn_post on Post {
    id
    userViewedAt
    ...PostRow_post
  }
`);

const USE_UPDATE_LOCAL_VIEWED_POST_FRAGMENT = gql(`
  fragment useUpdateLocalViewedPost_post on Post {
    ...useViewedPostsColumn_post
  }
`);

const VIEWED_POSTS_QUERY = gql(`
  query viewedPosts($afterCursor: String) {
    session: currentSession {
      user {
        id
        viewedPosts(first: 25, after: $afterCursor) {
          edges {
            node {
              ...useViewedPostsColumn_post
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
`);

export const useUpdateLocalViewedPost = (postId: string | undefined) => {
  const client = useApolloClient();

  useEffect(() => {
    if (!postId) {
      return;
    }

    const post = client.readFragment({
      id: `Post:${postId}`,
      fragment: USE_UPDATE_LOCAL_VIEWED_POST_FRAGMENT,
      fragmentName: 'useUpdateLocalViewedPost_post',
    });

    if (!post) {
      return;
    }

    const viewedData = client.readQuery({ query: VIEWED_POSTS_QUERY });

    if (!viewedData?.session.user.viewedPosts) {
      return;
    }

    const updateViewedPostsConnection = ConnectionHelper.insertNodeBefore(
      viewedData.session.user.viewedPosts,
      post,
      { skipUpdate: true },
    );

    client.writeQuery({
      query: VIEWED_POSTS_QUERY,
      data: {
        session: {
          ...viewedData.session,
          user: {
            ...viewedData.session.user,
            viewedPosts: {
              ...updateViewedPostsConnection,
              edges: _.uniqBy(updateViewedPostsConnection.edges, 'node.id'),
            },
          },
        },
      },
    });
  }, [client, postId]);
};

const useViewedPostsColumn = () => {
  const [activePreviewId, setActivePreviewId] = useState<string | null>(null);

  const result = useQuery(VIEWED_POSTS_QUERY);

  const fetchMore = ConnectionHelper.generateFetchMore(
    VIEWED_POSTS_QUERY,
    result,
    'session.user.viewedPosts',
  );
  const { data, loading } = result;
  const posts = _.compact(
    data?.session.user.viewedPosts?.edges?.map((edge) => edge?.node),
  );

  return {
    seamless: true,
    title: 'Viewed',
    children:
      !loading && posts.length === 0 ? (
        <EmptyState
          imageName="posts"
          title="Haven't read anything yet."
          message="Browse around. There's something interesting in here somewhere."
        />
      ) : (
        <div role="feed" aria-label="Recently viewed posts">
          {posts.map((post, index) => (
            <PostRow
              key={post.id}
              post={post}
              inList={index > 0}
              activePreviewId={activePreviewId}
              onActivePreviewIdChange={setActivePreviewId}
            />
          ))}
          <InfiniteScrollWaypoint fetchMore={fetchMore} />
        </div>
      ),
  };
};

export default useViewedPostsColumn;
